import { Chip, Grid, MenuItem, Paper, Table, TableBody, TableContainer, TableHead, TablePagination, TableRow, Button, Tooltip } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import toastr from "toastr";
import { InfoTooltip, ListTableCell, ListTableRow, SearchTableCell, NoMaxWidthTooltip } from '../../../components/CustomStyles/StyledComponents';
import { deleteAdminReport, getAllAdminReports, resetResponseState, setAdminFarmId, setLoading, setReportId, unsetLoading,getAdminFarm , clearAllAdminReportsData } from '../../../store/appAction';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteReportModal from '../../../components/Modal/DeleteReportModal';
import { toastrCustomOptions, toastrCustomOptionsLonger,toastrCustomOptionsNoFarmSelectPopUp } from '../../../constants/toastrOptions';
import { REPORT_STATUS_OPTIONS } from '../../../constants/arrays';
import EmptyTableBody from '../../../components/Tables/EmptyTableBody';
import SortButton from '../../../components/Buttons/SortButton';
import TableSearchText from '../../../components/Inputs/TableSearchText';
import { debounce } from 'lodash';
import TableSearchDatePicker from '../../../components/Inputs/TableSearchDatePicker';
import TableSearchSelect from '../../../components/Inputs/TableSearchSelect';
import SelectedFarm from '../../../components/SelectedFarm/SelectedFarm';
import { ReportStatus } from '../../../helpers/reportResultsHelper';
import { endpoints } from '../../../api/endpoints';
import * as api from '../../../api/base';
import {saveAs} from 'file-saver';
import queryString from 'query-string';

const SORTING = 'friendlyReportId';
const SORT_ORDER = 'asc';
const PAGE = 0;
const ROWS_PER_PAGE = 10;

const Reports = () => {
    dayjs.extend(utc);
    toastr.options = toastrCustomOptions;
    let navigate = useNavigate();
    const dispatch = useDispatch();

    const { successMsg, errorMsg } = useSelector(state => state.common);
    const reportList = useSelector(state => state.adminReports.reports);
    const { loading } = useSelector(state => state.common);
    const permissions = useSelector(state => state.auth.permissions);  

    const [anchorEl, setAnchorEl] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [currentReport, setCurrentReport] = useState(false);
    const [searchQuery, setSearchQuery] = useState({
        sorting: SORTING,
        sortOrder: SORT_ORDER,
        skipCount: PAGE,
        maxResultCount: ROWS_PER_PAGE
    });
    const [pagination, setPagination] = useState({
        page: PAGE,
        rowsPerPage: ROWS_PER_PAGE
    });
    const [skip, setSkip] = useState(false);

    const menuOpen = Boolean(anchorEl);

    useEffect(() => {
        dispatch(setLoading());
        return () => { 
            dispatch(clearAllAdminReportsData());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        reportList && setTimeout(() => {
            setSkip(false);
            dispatch(unsetLoading());
        }, 400);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportList]);

    useEffect(() => {
        const { page, rowsPerPage } = pagination;

        !skip && reportList?.items && setSearchQuery(prevState => ({
            ...prevState,
            skipCount: page * rowsPerPage,
            maxResultCount: rowsPerPage
        }));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination]);


    useEffect(() => {
        dispatch(getAllAdminReports(searchQuery));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    useEffect(() => {
        if (successMsg || errorMsg) {
            dispatch(unsetLoading());
            if (successMsg) {
                toastr.success(successMsg);
                dispatch(getAllAdminReports(searchQuery));
            }
            errorMsg && toastr.error(errorMsg, undefined, toastrCustomOptionsLonger);
            dispatch(resetResponseState());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [successMsg, errorMsg]);

    const handleChangePage = (event, newPage) => {
        setPagination(prevState => ({
            ...prevState,
            page: newPage
        }));
    };

    const handleChangeRowsPerPage = (event) => {
        setPagination(prevState => ({
            ...prevState,
            page: 0,
            rowsPerPage: parseInt(event.target.value, 10)
        }));
    };

    const handleDelete = async (event, reportId) => {
        setAnchorEl(null);
        setCurrentReport(reportId);
        if(reportId){
            const response = await api.get(endpoints.farmReport.settings.concat('/', reportId));
            if (response && response.status === 200) {
              if(response?.data?.isUpgraded === false){
                toastr.warning("This farm report cannot be updated as it belongs to a user that has not been approved for Agrecalc cloud.", undefined, toastrCustomOptionsNoFarmSelectPopUp);
              }else{
                setDeleteModalOpen(true);
              }
            }
          }
    }

    const exportData = async () => {
        dispatch(setLoading());
        const response = await api.getFileAsBlob(endpoints.reportExport.monthlyReport.concat('?', queryString.stringify(searchQuery)));
        if (response && response.status === 200) {
            const blob = new Blob([response.data], {
                type: 'application/vnd.ms-excel'
            });
            saveAs(blob, `Report.xlsx`);
            dispatch(unsetLoading());

        }else{
            dispatch(unsetLoading());
            toastr.error("An error occurred while attempting to export your data. Please try again.", undefined, toastrCustomOptionsNoFarmSelectPopUp);
        }
    }

    const handleDeleteModalResponse = (positiveResponse) => {
        setDeleteModalOpen(false);
        if (positiveResponse) {
            dispatch(deleteAdminReport(currentReport));
        }
    }

    const handleFarmEditDetails = (event, farmId) => {
        dispatch(setReportId(null));
        dispatch(setAdminFarmId(farmId));
        navigate('/admin/farm/edit')

    }

    const handleReportEditDetails = (event, reportId, farmId) => {
        if(Object.values(permissions ?? {}).some(value => ['AgreCalc.Reports', 'AgreCalc.Reports.Read', 'AgreCalc.Reports.Update'].includes(value))){
            dispatch(setAdminFarmId(farmId));
            dispatch(getAdminFarm(farmId));
            dispatch(setReportId(reportId));
            navigate('/data-entry/report/edit');
        }
    }

    const setSorting = ({ sortOrder, sorting }) => {
        setSearchQuery(prevState => ({
            ...prevState,
            sortOrder, sorting
        }));
    }

    const buildSearchQuery = (key, value) => {
        setSkip(true);
        setPagination({
            page: 0,
            rowsPerPage: 10
        });
        setSearchQuery(prevState => ({
            ...prevState,
            [key]: value || undefined,
            skipCount: PAGE,
            maxResultCount: ROWS_PER_PAGE
        }));
        // dispatch(setLoading());
    };

    const debouncedBuildSearchQuery = useMemo(() => {
        return debounce(buildSearchQuery, 500);
    }, []);

    // useEffect(() => {
    //     return () => {
    //         debouncedBuildSearchQuery.cancel();
    //     };
    // });

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={8} lg={9}>
                <Paper
                    style={{ paddingTop:'0px' }}
                    sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <SelectedFarm />
                </Paper>
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
                <Paper
                style={{ paddingTop:'0px' }}
                    sx={{
                        p: 2,
                        flexDirection: 'column',
                        textAlign: 'center'
                    }}
                >
                {
                    // Following code line has comment out due to the AGC-2313
                    //Object.values(permissions ?? {}).some(value => ['AgreCalc.Reports.ExportCSV'].includes(value)) &&
                    <Button variant="contained"
                        disabled={loading}
                        sx={{ textTransform: 'none' }}
                        onClick={(e) => exportData()}>
                        Export Data
                    </Button>
                }
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ width: '100%' }} size="small" aria-label={`Reports List Table`}>
                            <TableHead>
                                <TableRow>
                                    <ListTableCell sx={{ textAlign: '', width: '6%' }} aria-label={`Report ID Header`}>
                                        Report ID
                                        <SortButton
                                            sortKey={'friendlyReportId'}
                                            currentSortKey={searchQuery?.sorting}
                                            handleSorting={setSorting}
                                            aria-label={`Sort by Report ID`}
                                        />
                                    </ListTableCell>
                                    <ListTableCell sx={{ textAlign: 'left', width: '6%' }} aria-label={`Farm ID Header`}>
                                        Farm ID
                                        <SortButton
                                            sortKey={'friendlyFarmId'}
                                            currentSortKey={searchQuery?.sorting}
                                            handleSorting={setSorting}
                                            aria-label={`Sort by Farm ID`}
                                        />
                                    </ListTableCell>
                                    <ListTableCell sx={{ textAlign: 'left', width: '10%' }} aria-label={`Farm Name Header`} >
                                        Farm Name
                                        <SortButton
                                            sortKey={'farmName'}
                                            currentSortKey={searchQuery?.sorting}
                                            handleSorting={setSorting}
                                            aria-label={`Sort by Farm Name`}
                                        />
                                    </ListTableCell>
                                    <ListTableCell sx={{ textAlign: 'left', width: '15%' }} aria-label={`Report Name Header`}>
                                        Reference
                                        <SortButton
                                            sortKey={'reference'}
                                            currentSortKey={searchQuery?.sorting}
                                            handleSorting={setSorting}
                                            aria-label={`Sort by Report Name`}
                                        />
                                    </ListTableCell>
                                    <ListTableCell sx={{ textAlign: 'left', width: '9%' }} aria-label={`Year End Header`} >
                                        Year End
                                        <SortButton
                                            sortKey={'yearEnd'}
                                            currentSortKey={searchQuery?.sorting}
                                            handleSorting={setSorting}
                                            aria-label={`Sort by Year End`}
                                        />
                                    </ListTableCell>
                                    <ListTableCell sx={{ textAlign: 'left', width: '9%' }} aria-label={`Organisation ID Header`}>
                                        Organisation
                                        <SortButton
                                            sortKey={'organisation'}
                                            currentSortKey={searchQuery?.sorting}
                                            handleSorting={setSorting}
                                            aria-label={`Sort by Organisation`}
                                        />
                                    </ListTableCell>
                                    <ListTableCell sx={{ textAlign: 'left', width: '7%' }} aria-label={`Office Header`}>
                                        Office
                                        <SortButton
                                            sortKey={'office'}
                                            currentSortKey={searchQuery?.sorting}
                                            handleSorting={setSorting}
                                            aria-label={`Sort by Office`}
                                        />
                                    </ListTableCell>
                                    <ListTableCell sx={{ textAlign: 'left', width: '11%' }} aria-label={`Projects ID Header`}>
                                        Projects
                                    </ListTableCell>
                                    <ListTableCell sx={{ textAlign: 'left', width: '8%' }} aria-label={`Status Header`}>
                                        Status
                                        <SortButton
                                            sortKey={'status'}
                                            currentSortKey={searchQuery?.sorting}
                                            handleSorting={setSorting}
                                            aria-label={`Sort by Status`}
                                        />
                                        <NoMaxWidthTooltip title={ReportStatus.renderStatusTooltip()}>
                                            <InfoOutlinedIcon  color='primary' sx={{ marginLeft: '5px', verticalAlign: 'middle' , cursor: 'pointer'}} fontSize='small' />
                                        </NoMaxWidthTooltip>
                                    </ListTableCell>
                                    <ListTableCell sx={{ textAlign: 'left', width: '7%' }} aria-label={`Updated On Header`}>
                                        Updated On
                                        <SortButton
                                            sortKey={'lastUpdated'}
                                            currentSortKey={searchQuery?.sorting}
                                            handleSorting={setSorting}
                                            aria-label={`Sort by Last Updated`}
                                        />
                                    </ListTableCell>
                                    <ListTableCell sx={{ textAlign: 'left', width: '6%' }} aria-label={`Updated By Header`}>
                                        Updated By
                                        <SortButton
                                            sortKey={'lastUpdatedBy'}
                                            currentSortKey={searchQuery?.sorting}
                                            handleSorting={setSorting}
                                            aria-label={`Sort by Last Updated By`}
                                        />
                                    </ListTableCell>
                                    <ListTableCell sx={{ textAlign: 'center', width: '6%' }} aria-label={`Action Header`} >
                                        Action
                                    </ListTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <ListTableRow>
                                    <SearchTableCell>
                                        <TableSearchText
                                            searchKey="friendlyReportId"
                                            handleSearchQuery={debouncedBuildSearchQuery}
                                            autoFocus={true}
                                            aria-label={`Search by Report ID`}
                                        />
                                    </SearchTableCell>
                                    <SearchTableCell>
                                        <TableSearchText
                                            searchKey="friendlyFarmId"
                                            handleSearchQuery={debouncedBuildSearchQuery}
                                            aria-label={`Search by Farm ID`}
                                        />
                                    </SearchTableCell>
                                    <SearchTableCell>
                                        <TableSearchText
                                            searchKey="farmName"
                                            handleSearchQuery={debouncedBuildSearchQuery}
                                            aria-label={`Search by Farm name`}
                                        />
                                    </SearchTableCell>
                                    <SearchTableCell>
                                        <TableSearchText
                                            searchKey="reference"
                                            handleSearchQuery={debouncedBuildSearchQuery}
                                            aria-label={`Search by Report reference`}
                                        />
                                    </SearchTableCell>
                                    <SearchTableCell>
                                        <TableSearchDatePicker
                                            inputFormat='MM-YYYY'
                                            views={['month', 'year']}
                                            searchKey="yearEnd"
                                            handleSearchQuery={debouncedBuildSearchQuery}
                                            aria-label={`Search by Year End`}
                                        />
                                    </SearchTableCell>
                                    <SearchTableCell>
                                        <TableSearchText
                                            searchKey="organisation"
                                            handleSearchQuery={debouncedBuildSearchQuery}
                                            aria-label={`Search by Organisation`}
                                        />
                                    </SearchTableCell>
                                    <SearchTableCell>
                                        <TableSearchText
                                            searchKey="office"
                                            handleSearchQuery={debouncedBuildSearchQuery}
                                            aria-label={`Search by Office`}
                                        />
                                    </SearchTableCell>
                                    <SearchTableCell>
                                        <TableSearchText
                                            searchKey="projects"
                                            handleSearchQuery={debouncedBuildSearchQuery}
                                            aria-label={`Search by Project`}
                                        />
                                    </SearchTableCell>
                                    <SearchTableCell>
                                        <TableSearchSelect
                                            searchKey="status"
                                            handleSearchQuery={debouncedBuildSearchQuery}
                                            aria-label={`Search by Report Status`}
                                        >
                                            {REPORT_STATUS_OPTIONS.map((option) => (
                                                <MenuItem key={option.key} value={option.value}>{option.key}</MenuItem>
                                            ))}
                                        </TableSearchSelect>
                                    </SearchTableCell>
                                    <SearchTableCell>
                                        <TableSearchDatePicker
                                            searchKey="lastUpdated"
                                            handleSearchQuery={debouncedBuildSearchQuery}
                                            aria-label={`Search by Last Updated Date`}
                                        />
                                    </SearchTableCell>
                                    <SearchTableCell>
                                        <TableSearchText
                                            searchKey="lastUpdatedBy"
                                            handleSearchQuery={debouncedBuildSearchQuery}
                                            aria-label={`Search by Last updated by`}
                                        />
                                    </SearchTableCell>
                                    <SearchTableCell></SearchTableCell>
                                </ListTableRow>
                                {reportList?.items?.length > 0 ?
                                    (reportList?.items || []).map((row, index) => (
                                        <ListTableRow key={row.reportId}
                                          aria-label={`Reports row ${index}`}>
                                            <ListTableCell
                                                sx={{ cursor: 'pointer' }}
                                                aria-label={`${index} - Report ID`}
                                                onClick={(e) => handleReportEditDetails(e, row.reportId, row.farmId)}>
                                                    <NoMaxWidthTooltip title={`Click to go to "${row.reference}"`}>
                                                        {row.friendlyReportId}
                                                    </NoMaxWidthTooltip>
                                            </ListTableCell>
                                            <ListTableCell
                                                sx={{ cursor: 'pointer' }}
                                                aria-label={`${index} - Farm ID`}
                                                onClick={(e) => handleFarmEditDetails(e, row.farmId)}>
                                                <NoMaxWidthTooltip title={`Click to go to "${row.farmName}" farm`}>
                                                    {row.friendlyFarmId}
                                                </NoMaxWidthTooltip>
                                            </ListTableCell>
                                            <ListTableCell
                                                sx={{ cursor: 'pointer' }}
                                                aria-label={`${index} - Farm Name`}
                                                onClick={(e) => handleFarmEditDetails(e, row.farmId)}>
                                                <NoMaxWidthTooltip title={`Click to go to "${row.farmName}" farm`}>
                                                    {row.farmName}
                                                </NoMaxWidthTooltip>
                                            </ListTableCell>
                                            <ListTableCell
                                                sx={{ cursor: 'pointer' }}
                                                aria-label={`${index} - Report Name`}
                                                onClick={(e) => handleReportEditDetails(e, row.reportId, row.farmId)}>
                                                    <NoMaxWidthTooltip title={`Click to go to "${row.reference}"`}>
                                                        {row.reference}
                                                    </NoMaxWidthTooltip>
                                            </ListTableCell>
                                            <ListTableCell aria-label={`${index} - Year End`} >{dayjs(row.yearEnd).format('MMMM YYYY')}</ListTableCell>
                                            <ListTableCell aria-label={`${index} - Organisation`} >{row.organisation}</ListTableCell>
                                            <ListTableCell aria-label={`${index} - Office`}>{row.office}</ListTableCell>
                                            <ListTableCell aria-label={`${index} - Projects`}>
                                                {row.projects.map((project,index) => (
                                                    <Tooltip key={row.reportId+index} title={project}>
                                                    <Chip key={row.reportId+index}
                                                        sx={{ m: 0.2,
                                                                fontSize: '0.6rem', 
                                                                maxWidth: 125,
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis"}}
                                                        size="small"
                                                        color="success"
                                                        label={project}
                                                        variant="outlined" />
                                                    </Tooltip>
                                                ))
                                                }
                                            </ListTableCell >
                                            { ReportStatus.getColorTableCell(row.status,`${index} - Status`) }
                                            <ListTableCell aria-label={`${index} - Last Updated`}>{row.lastUpdated ? dayjs(row.lastUpdated).format('YYYY/MM/DD HH:mm') : '--'}</ListTableCell>
                                            <ListTableCell aria-label={`${index} - Last Updated by`}>{row.lastUpdatedBy}</ListTableCell>
                                            <ListTableCell sx={{ textAlign: 'center' }} aria-label={`${index} - Actions`}>
                                                <InfoTooltip title="Delete Report">
                                                    <DeleteIcon aria-label={`${index} - Delete`}
                                                        id='delete'
                                                        sx={{ cursor: 'pointer' }}
                                                        aria-controls={menuOpen}
                                                        onClick={(e) => handleDelete(e, row.reportId)}
                                                    />
                                                </InfoTooltip>
                                            </ListTableCell>
                                        </ListTableRow>
                                    ))
                                    : <EmptyTableBody title="reports" />}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={reportList?.totalCount?reportList?.totalCount:0}
                        rowsPerPage={pagination.rowsPerPage}
                        page={pagination.page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                    <DeleteReportModal
                        isOpen={deleteModalOpen}
                        handleResponse={handleDeleteModalResponse} />
                </Paper>
            </Grid>
        </Grid>
    )
}

export default Reports